
import { useState, useMemo } from "react";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import { type GenericId as Id } from "convex/values";
import { App } from "../types/app";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { AppIcon } from "./AppIcon";
import { Search, Package, Smartphone } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface DeviceAppsInstalledContentProps {
  deviceId: string;
}

export const DeviceAppsInstalledContent = ({ deviceId }: DeviceAppsInstalledContentProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Query installed apps for the device
  const installedApps = useConvexQuery(api.apps.list.installedOnDevice, {
    device: deviceId as Id<"devices">
  });

  console.log('DeviceAppsInstalledContent - deviceId:', deviceId);
  console.log('DeviceAppsInstalledContent - installedApps:', installedApps);

  // Filter apps based on search term
  const filteredApps = useMemo(() => {
    if (!installedApps) return [];
    
    return installedApps.filter((app: any) =>
      (app.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.category || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [installedApps, searchTerm]);

  if (installedApps === undefined) {
    return (
      <div className="space-y-6 h-full flex flex-col">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Installed Apps</h3>
          <p className="text-gray-600">Loading installed apps...</p>
        </div>
        
        <Card className="flex-1">
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={`loading-${i}`} className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gray-200 rounded"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!installedApps || installedApps.length === 0) {
    return (
      <div className="space-y-6 h-full flex flex-col">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Installed Apps</h3>
          <p className="text-gray-600">Apps currently installed on this device</p>
        </div>
        
        <Card className="flex-1 flex items-center justify-center">
          <CardContent className="p-12 text-center">
            <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Installed Apps Found</h4>
            <p className="text-gray-500">
              No apps are currently installed on this device, or the device hasn't synced recently.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-full flex flex-col">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Installed Apps</h3>
        <p className="text-gray-600">
          Apps currently installed on this device ({installedApps.length} total)
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search installed apps..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Apps Table */}
      <Card className="flex-1 flex flex-col">
        <CardContent className="p-0 flex-1 flex flex-col">
          <ScrollArea className="flex-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>App Name</TableHead>
                  <TableHead>Category</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8">
                      <div className="flex flex-col items-center">
                        <Package className="w-8 h-8 text-gray-400 mb-2" />
                        <p className="text-gray-500">
                          {searchTerm ? "No apps match your search" : "No apps found"}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApps.map((app: any) => (
                    <TableRow key={app._id} className="hover:bg-gray-50">
                      <TableCell>
                        <AppIcon 
                          app_icon_url={app.iconUrl} 
                          app_name={app.name} 
                          size="sm" 
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">
                          {app.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {app.category || "Uncategorized"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Results count */}
      {searchTerm && (
        <div className="text-sm text-gray-500 text-center">
          Showing {filteredApps.length} of {installedApps.length} apps
        </div>
      )}
    </div>
  );
};
